import * as React from 'react';
import { Plus, Trash2, RefreshCw, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  listNotificationTemplates,
  saveNotificationTemplate,
  deleteNotificationTemplate,
  adminSendNotification,
} from '@/api/client';

type Template = {
  id: number;
  template_key: string;
  name: string;
  title_template: string;
  content_template: string;
  is_active: number;
  updated_at: string;
};

type Draft = {
  template_key: string;
  name: string;
  title_template: string;
  content_template: string;
  is_active: boolean;
};

export function NotificationTemplatesTab() {
  const [templates, setTemplates] = React.useState<Template[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');

  // 编辑态
  const [editing, setEditing] = React.useState<Draft | null>(null);
  const [saving, setSaving] = React.useState(false);

  // 发送态
  const [sendTo, setSendTo] = React.useState('');
  const [sendTitle, setSendTitle] = React.useState('');
  const [sendContent, setSendContent] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await listNotificationTemplates();
      setTemplates(d.items || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openEdit = React.useCallback((t?: Template) => {
    setMsg(''); setErr('');
    if (t) {
      setEditing({
        template_key: t.template_key, name: t.name,
        title_template: t.title_template, content_template: t.content_template,
        is_active: !!t.is_active,
      });
    } else {
      setEditing({ template_key: '', name: '', title_template: '', content_template: '', is_active: true });
    }
  }, []);

  const handleSave = React.useCallback(async () => {
    if (!editing) return;
    if (!editing.template_key.trim() || !editing.name.trim()) { setErr('模板标识和名称必填'); return; }
    setSaving(true); setMsg(''); setErr('');
    try {
      await saveNotificationTemplate(editing);
      setMsg('模板已保存');
      setEditing(null);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [editing, load]);

  const handleDelete = React.useCallback(async (key: string) => {
    if (!confirm(`确定删除模板 ${key}？`)) return;
    try {
      await deleteNotificationTemplate(key);
      load();
    } catch { /* ignore */ }
  }, [load]);

  const handleSend = React.useCallback(async () => {
    if (!sendTitle.trim() && !sendContent.trim()) { setErr('请输入通知标题或内容'); return; }
    setSending(true); setMsg(''); setErr('');
    try {
      const r = await adminSendNotification({
        username: sendTo.trim() || '',
        title: sendTitle.trim(),
        content: sendContent.trim(),
      });
      setMsg(r.message || '已发送');
      setSendTo(''); setSendTitle(''); setSendContent('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : '发送失败');
    } finally {
      setSending(false);
    }
  }, [sendTo, sendTitle, sendContent]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>通知模板</CardTitle>
            <CardDescription>配置系统自动通知的标题与内容（支持 {`{变量}`} 占位）</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={load} title="刷新"><RefreshCw className="h-4 w-4" /></Button>
            <Button size="sm" onClick={() => openEdit()}><Plus className="h-3.5 w-3.5" /> 新建模板</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {msg && <div className="rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">{msg}</div>}
        {err && <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div>}

        {/* 模板列表 */}
        <div className="rounded-md border">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">加载中…</div>
          ) : templates.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">暂无模板</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium">标识</th>
                  <th className="px-4 py-2.5 text-left font-medium">名称</th>
                  <th className="px-4 py-2.5 text-left font-medium">标题模板</th>
                  <th className="px-4 py-2.5 text-left font-medium">内容模板</th>
                  <th className="px-4 py-2.5 text-left font-medium">状态</th>
                  <th className="px-4 py-2.5 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.template_key} className="border-b last:border-0">
                    <td className="px-4 py-2.5 font-mono text-xs">{t.template_key}</td>
                    <td className="px-4 py-2.5">{t.name}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.title_template || '—'}</td>
                    <td className="px-4 py-2.5 max-w-[260px] truncate text-xs text-muted-foreground">{t.content_template || '—'}</td>
                    <td className="px-4 py-2.5">
                      {t.is_active ? <Badge variant="success">启用</Badge> : <Badge variant="secondary">停用</Badge>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(t)}>编辑</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => handleDelete(t.template_key)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 编辑模板 */}
        {editing && (
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-sm font-medium">模板标识</Label>
                <Input className="mt-1" placeholder="如 credit_recharge" value={editing.template_key}
                  onChange={(e) => setEditing({ ...editing, template_key: e.target.value })} disabled={templates.some(t => t.template_key === editing.template_key)} />
              </div>
              <div>
                <Label className="text-sm font-medium">名称</Label>
                <Input className="mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium">标题模板</Label>
                <Input className="mt-1" value={editing.title_template} onChange={(e) => setEditing({ ...editing, title_template: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium">启用</Label>
                <label className="mt-2 flex items-center gap-2">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                  <span className="text-sm text-muted-foreground">启用此模板</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">内容模板（支持 {`{amount} {balance} {action} {reason} {title} {content}`}）</Label>
                <textarea className="mt-1 min-h-[90px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={editing.content_template} onChange={(e) => setEditing({ ...editing, content_template: e.target.value })} />
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>取消</Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? '保存中…' : '保存'}</Button>
            </div>
          </div>
        )}

        {/* 手动发送 */}
        <div className="rounded-md border p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Send className="h-3.5 w-3.5" /> 手动发送通知
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label className="text-xs font-medium">指定用户（留空=全部活跃用户）</Label>
              <Input className="mt-1" placeholder="username" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-medium">标题</Label>
              <Input className="mt-1" value={sendTitle} onChange={(e) => setSendTitle(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-medium">内容</Label>
              <Input className="mt-1" value={sendContent} onChange={(e) => setSendContent(e.target.value)} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={handleSend} disabled={sending}>{sending ? '发送中…' : '发送'}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
